import * as Sentry from "@sentry/react"

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined
const ENVIRONMENT = (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? "production"
const RELEASE = (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) ?? undefined

/**
 * Edge-function error codes that represent expected 4xx validation/auth
 * rejections. These are protocol responses, not bugs — drop them from Sentry
 * so noisy invite/revoke flows don't bury real exceptions. Server 5xx codes
 * (e.g. *_FAILED, INTERNAL) are intentionally NOT in this list.
 */
const EXPECTED_VALIDATION_CODES = new Set<string>([
  "EMAIL_EXISTS",
  "INVALID_EMAIL",
  "INVITATION_PENDING",
  "RATE_LIMITED",
  "FORBIDDEN",
  "FORBIDDEN_ROLE",
  "FORBIDDEN_TENANT",
  "WAREHOUSE_REQUIRED",
  "INVALID_PRIMARY_COUNT",
  "BUSINESS_REQUIRED",
  "BUSINESS_ROLE_REQUIRED",
  "BUSINESS_NOT_FOUND",
  "BUSINESS_TENANT_MISMATCH",
  "INVALID_BUSINESS_NAME",
  "NOT_PENDING",
  "NOT_FOUND",
  "EXPIRED",
  "INVITATION_EXPIRED",
  "RESEND_TOO_SOON",
  "INVALID_REQUEST",
  "INVALID_TENANT",
  "TENANT_NOT_FOUND",
  "UNAUTHORIZED",
])

function hasExpectedCode(value: unknown): boolean {
  if (!value || typeof value !== "object") return false
  const code = (value as { code?: unknown }).code
  return typeof code === "string" && EXPECTED_VALIDATION_CODES.has(code)
}

const PII_DENYLIST = [
  /password/i,
  /token/i,
  /authorization/i,
  /idempotency[-_]?key/i,
  /unsubscribe[-_]?token/i,
  /stripe[-_]?signature/i,
  /^cookie$/i,
] as const

function isDenied(key: string): boolean {
  return PII_DENYLIST.some((rx) => rx.test(key))
}

function scrub(value: unknown, depth = 0): unknown {
  if (depth > 6) return "[Truncated]"
  if (value == null) return value
  if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1))
  if (typeof value !== "object") return value
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = isDenied(k) ? "[Filtered]" : scrub(v, depth + 1)
  }
  return out
}

export function initSentry(): void {
  if (!DSN) {
    if (typeof console !== "undefined") {
      console.info("[Sentry] VITE_SENTRY_DSN not set; observability disabled.")
    }
    return
  }
  Sentry.init({
    dsn: DSN,
    environment: ENVIRONMENT,
    release: RELEASE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    beforeSend(event, hint) {
      // Drop expected validation/auth rejections (4xx protocol responses).
      // Real 5xx exceptions and uncoded errors still flow through.
      const original = hint?.originalException
      if (hasExpectedCode(original)) return null
      // Some thrown errors land on event.exception with the code on the
      // serialized values (rare path); guard there too.
      const values = event.exception?.values
      if (values?.some(v => typeof (v as any)?.code === "string"
        && EXPECTED_VALIDATION_CODES.has((v as any).code))) {
        return null
      }
      if (event.request?.headers) {
        event.request.headers = scrub(event.request.headers) as Record<string, string>
      }
      if (event.request?.data) {
        event.request.data = scrub(event.request.data)
      }
      if (event.extra) {
        event.extra = scrub(event.extra) as Record<string, unknown>
      }
      if (event.contexts) {
        event.contexts = scrub(event.contexts) as typeof event.contexts
      }
      return event
    },
  })
  Sentry.setTag("runtime", "browser")
}

/**
 * Sets the Sentry user + tenant tags. Must be called only after userProfile loads
 * (DemoContext.userProfile truthy). Pass home_tenant_id from userProfile.tenantId
 * and selected_tenant_id from selectedTenant.id; they may differ during impersonation.
 */
export function setSentryUser(input: {
  userId: string
  email: string
  role: string
  homeTenantId: string
  selectedTenantId: string
}): void {
  if (!DSN) return
  Sentry.setUser({ id: input.userId, email: input.email })
  Sentry.setTags({
    user_role: input.role,
    home_tenant_id: input.homeTenantId,
    tenant_id: input.selectedTenantId,
    impersonating: input.homeTenantId !== input.selectedTenantId ? "true" : "false",
  })
}

export function clearSentryUser(): void {
  if (!DSN) return
  Sentry.setUser(null)
}

export { Sentry }
